import { ReactNode } from "react";
import { z } from "zod";
import { DEFAULT_CURRENCY } from "../config/constants";
import {
  NOTIFICATION_RECIPIENT_TYPES_FOR_ADMIN,
  NOTIFICATION_RECIPIENT_TYPES_FOR_TEACHER,
  NOTIFICATION_TYPES_FOR_ADMIN,
  NOTIFICATION_TYPES_FOR_TEACHER,
  TFeedbackCategoryEnum,
  TGenderEnum,
  TPaymentMethodEnum,
  TPaymentStatusEnum,
  ZDatePreset,
} from "./types";

/////////////////
// Zod schemas //
/////////////////

// Reusable

const phoneNumberSchema = z
  .string()
  .trim()
  .min(11)
  .max(15)
  .regex(/^\d{11}$/, "Invalid phone number");

// Course

export const courseCreateSchema = z.object({
  title: z.string().trim(),
});

export const courseEditSchema = courseCreateSchema.extend({
  subtitle: z.string().trim().optional(),
  description: z.string().optional(),
  categoryId: z.string().optional(),
  originalPrice: z.coerce.number().nonnegative(),
  discountedPrice: z.coerce.number().nonnegative(),
  thumbnailImageId: z.string().trim().optional(),
  trailerVideoId: z.string().trim().optional(),
  isPublished: z.boolean().default(false),
});

export const courseDBSchema = courseEditSchema.extend({
  slug: z.string().trim(),
  teacherId: z.string(),
});

// Section

export const sectionCreateSchema = z.object({
  title: z.string().trim(),
});

export const sectionDBSchema = sectionCreateSchema.extend({
  slug: z.string().trim(),
  sortPosition: z.coerce.number(),
  courseId: z.string().uuid(),
});

// Chapter

export const chapterCreateSchema = z.object({
  title: z.string().trim(),
  description: z.string().trim().optional(),
  chapterVideoId: z.string().trim().optional(),
  isFree: z.boolean().default(false),
  isPublished: z.boolean().default(false),
});

export const chapterDBSchema = chapterCreateSchema.extend({
  slug: z.string().trim(),
  sortPosition: z.coerce.number(),
  sectionId: z.string().uuid(),
  courseId: z.string().uuid(),
});

// Offering

export const offeringCreateSchema = z.object({
  title: z.string().trim(),
});

export const offeringDBSchema = offeringCreateSchema.extend({
  sortPosition: z.coerce.number(),
  courseId: z.string().uuid(),
});

// Blog

export const blogCreateSchema = z.object({
  title: z.string().trim(),
});

export const blogEditSchema = blogCreateSchema.extend({
  subtitle: z.string().trim().optional().nullable(),
  content: z.string().optional().nullable(),
  categoryId: z.string().optional().nullable(),
  thumbnailImageId: z.string().trim().optional().nullable(),
  isPublished: z.boolean().default(false),
});

export const blogDBSchema = blogEditSchema.extend({
  slug: z.string().trim(),
  authorId: z.string(),
});

// User

export const userSchema = z.object({
  id: z.string(),
  email_addresses: z.array(
    z.object({
      email_address: z.string().email(),
    }),
  ),
  first_name: z.string(),
  last_name: z.string(),
  image_url: z.string().url(),
});

export const deletedUserSchema = z.object({
  object: z.string(),
  id: z.string().optional(), // no need for .nullable() here
  slug: z.string().optional(),
  deleted: z.boolean(),
});

// Affiliate

const baseAffiliateSchema = z.object({
  paymentMethod: z.enum(TPaymentMethodEnum, {
    required_error: "Payment method is required",
  }),
  phoneNumber: phoneNumberSchema.optional(),
  bankName: z.string().min(3).max(50).optional(),
  accountHolder: z.string().min(3).max(50).optional(),
  accountNumber: z.string().min(10).max(20).optional(),
  iban: z.string().min(24).max(34).optional(),
});

export const affiliateCreateSchema = baseAffiliateSchema.superRefine((data, ctx) => {
  if (data.paymentMethod !== "BANK_TRANSFER" && !data.phoneNumber) {
    ctx.addIssue({
      path: ["phoneNumber"],
      message: "Phone number is required for Easypaisa/JazzCash",
      code: "custom",
    });
  }

  if (data.paymentMethod === "BANK_TRANSFER") {
    (["bankName", "accountHolder", "accountNumber", "iban"] as const).forEach((field) => {
      if (!data[field]) {
        ctx.addIssue({
          path: [field],
          message: "This field is required for bank transfer",
          code: "custom",
        });
      }
    });
  }
});

export const affiliateDBSchema = baseAffiliateSchema.extend({
  referralCode: z.string(),
  userId: z.string(),
});

// Payment

export const paymentCreateSchema = z.object({
  phoneNumber: phoneNumberSchema,
  city: z.string().min(2).max(100),
  age: z.coerce.number().min(10).max(100),
  gender: z.enum(TGenderEnum),
  // paymentMethod: z.enum(TPaymentMethodEnum).optional(),
  receiptImageId: z.string().trim(),
});

export const paymentDBSchema = paymentCreateSchema.extend({
  amount: z.number(),
  currency: z.literal(DEFAULT_CURRENCY),
  status: z.enum(TPaymentStatusEnum),
  courseId: z.string().trim().uuid("Invalid course ID"),
  userId: z.string().trim(),
});

// Review

export const courseReviewCreateSchema = z.object({
  description: z.string().trim().optional().nullable(),
  rating: z.coerce
    .number({ message: "Rating must be a numeric value" })
    .nonnegative({ message: "Rating cannot be a negative value" })
    .min(1)
    .max(5),
});

export const courseReviewDBSchema = courseReviewCreateSchema.extend({
  courseId: z.string(),
  userId: z.string(),
});

// Comment

export const commentCreateSchema = z.object({
  content: z.string(),
});

// Category

export const categoryCreateSchema = z.object({
  title: z.string().trim(),
});

// Newsletter

export const newsletterSubscriptionSchema = z.object({
  email: z.string(),
  firstName: z.string().optional(),
});

// Feedback

export const feedbackCreateSchema = z.object({
  content: z.string(),
  category: z.enum(TFeedbackCategoryEnum).optional(),
});

export const feedbackReplySchema = z.object({
  response: z.string(),
});

// Media

export const localFileSchema = z.object({
  name: z.string(),
  size: z.coerce.number(),
  type: z.string(),
  timestamp: z.coerce.number(),
});

export const originalFileSchema = z.object({
  originalName: z.string(),
  originalSize: z.coerce.number(),
  originalType: z.string(),
  originalTimestamp: z.coerce.number(),
});

export const uploadthingFileSchema = z.object({
  key: z.string(),
  name: z.string(),
  size: z.coerce.number(),
  type: z.string(),
  url: z.string(),
  category: z.string().optional(),
});

export const imageCreateSchema = z.object({
  key: z.string(),
  path: z.string(),
  url: z.string().url(),
  category: z.string(),
  hostingService: z.string(),

  originalName: z.string(),
  originalSize: z.coerce.number().int().positive(),
  originalType: z.string(),
  originalTimestamp: z.coerce.bigint(),

  userId: z.string(),
});

export const videoCreateSchema = z.object({
  platform: z.string().min(1),
  duration: z.coerce.number().int().nonnegative(),

  title: z.string().min(1).nullable().optional(),
  size: z.coerce.number().int().positive().optional(),
  resolution: z.string().nullable().optional(),
  frameRate: z.coerce.number().int().positive().optional(),
  width: z.coerce.number().int().positive().optional(),
  height: z.coerce.number().int().positive().optional(),
  aspectRatio: z.string().optional(),
  animatedGifUrl: z.string().url().optional(),
  thumbnailUrl: z.string().url().nullable().optional(),
  codecs: z.string().optional(),

  // External
  externalVideoId: z.string().optional(),

  // Mux
  muxAssetId: z.string().optional(),
  muxPlaybackId: z.string().optional(),
  muxEncodingTier: z.string().optional(),
  muxEncodingStatus: z.string().optional(),
  muxCreatedAt: z.string().optional(),

  // Bunny
  bunnyVideoGuid: z.string().optional(),
  bunnyStatus: z.coerce.number().int().optional(),

  // File
  originalName: z.string().optional(),
  originalSize: z.coerce.number().int().positive().optional(),
  originalType: z.string().optional(),
  originalTimestamp: z.coerce.bigint().optional(),

  userId: z.string().min(1),
});

export const uploadthingFilesArraySchema = z.array(uploadthingFileSchema);

// Notification

export const notificationCreateSchema = z.object({
  type: z.string().min(1),
  title: z.string().min(1),
  content: z.string().min(1),
  actionUrl: z.string().url(),
  iconUrl: z.string().url().optional(),
  thumbnailUrl: z.string().url().optional(),
  targetScope: z.string().min(1),
  targetId: z.string().optional(),
});

const ntfa = NOTIFICATION_TYPES_FOR_ADMIN;
const nrtfa = NOTIFICATION_RECIPIENT_TYPES_FOR_ADMIN;

export const customNotificationSchemaForAdmin = z.object({
  type: z.enum(ntfa.map((type) => type.id) as [(typeof ntfa)[number]["id"]]),
  title: z.string(),
  content: z.string(),
  actionUrl: z.string(),
  iconUrl: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  targetScope: z.enum(nrtfa.map((type) => type.id) as [(typeof nrtfa)[number]["id"]]),
  targetId: z.string().optional(),
  recipientIds: z.array(z.string()).optional(),
});

const ntft = NOTIFICATION_TYPES_FOR_TEACHER;
const nrtft = NOTIFICATION_RECIPIENT_TYPES_FOR_TEACHER;

export const customNotificationSchemaForTeacher = z.object({
  type: z.enum(ntft.map((type) => type.id) as [(typeof ntft)[number]["id"]]),
  title: z.string(),
  content: z.string(),
  actionUrl: z.string(),
  iconUrl: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  targetScope: z.enum(nrtft.map((type) => type.id) as [(typeof nrtft)[number]["id"]]),
  targetId: z.string().optional(),
  recipientIds: z.array(z.string()).optional(),
});

// Static content

export const staticContentSchema = z.object({
  staticContent: z.any(),
});

export const genericFieldSchema = z.object({
  title: z.string(),
  key: z.string(),
  type: z.string(),
});

const baseFieldGroupSchema = z.object({
  title: z.string(),
  key: z.string(),
  expandable: z.boolean().optional(),
  singleItem: z.string().optional(),
  fields: z.array(genericFieldSchema),
});

type ZFieldGroup = z.infer<typeof baseFieldGroupSchema> & {
  childGroups?: ZFieldGroup[];
};

export const fieldGroupSchema: z.ZodType<ZFieldGroup> = baseFieldGroupSchema.extend({
  childGroups: z.lazy(() => fieldGroupSchema.array()).optional(),
});

// Static content (Reusable)

export const stepSchema = z.object({
  label: z.string(),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string(),
});

export const projectSchema = z.object({
  title: z.string(),
  description: z.string(),
  imageUrl: z.string(),
});

export const reviewSchema = z.object({
  fullName: z.string(),
  username: z.string(),
  userImageUrl: z.string(),
  reviewText: z.string(),
  rating: z.number(),
});

export const featureSchema = z.object({
  title: z.string(),
  description: z.string(),
  icon: z.string(),
});

export const featureFullSchema = featureSchema.extend({
  iconBackgroundColor: z.string(),
  iconForegroundColor: z.string(),
});

export const faqSchema = z.object({
  question: z.string(),
  answer: z.string(),
});

export const enrollmentFeatureSchema = z.object({
  title: z.string(),
});

export const enrollmentFeatureAdditionalSchema = enrollmentFeatureSchema;

// Static content (Landing page)

export const landingPageContentSchema = z.object({
  // announcementBannerContent: z.object({
  //   title: z.string(),
  //   link: z.string(),
  // }),
  announcementBadgeContent: z.object({
    title: z.string(),
    link: z.string(),
  }),
  heroContent: z.object({
    title: z.string(),
    subtitle: z.string(),
    introVideoUrl: z.string(),
  }),
  primaryCourseContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    courseSlug: z.string(),
  }),
  coursesContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
  }),
  blogsContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
  }),
  featuresContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    features: z.array(featureFullSchema),
  }),
  reviewsContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    reviews: z.array(reviewSchema),
  }),
  aboutTheAuthorContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    richText: z.string(),
  }),
  newsletterContent: z.object({
    title: z.string(),
    description: z.string(),
  }),
  faqsContent: z.object({
    title: z.string(),
    faqs: z.array(faqSchema),
  }),
});

// Static content (Course page)

export const courseContentSchema = z.object({
  heroContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
  }),
  storyContent: z.object({
    label: z.string(),
    title: z.string(),
    richText: z.string(),
  }),
  introContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    imageUrl: z.string(),
  }),
  ourApproachContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    steps: z.array(stepSchema),
  }),
  projectsContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    projects: z.array(projectSchema),
  }),
  curriculumContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
  }),
  reviewsContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    reviews: z.array(reviewSchema),
  }),
  featuresContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    features: z.array(featureSchema),
  }),
  enrollContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    enrollmentFeatures: z.array(enrollmentFeatureSchema),
    enrollmentFeaturesAdditional: z.array(enrollmentFeatureAdditionalSchema),
  }),
  aboutTheAuthorContent: z.object({
    label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    richText: z.string(),
  }),
  faqsContent: z.object({
    title: z.string(),
    faqs: z.array(faqSchema),
  }),
});

// Table

export const columnDefSchema = <T>() =>
  z.object({
    title: z.string(),
    key: z.string(),
    sortValue: z
      .function()
      .args(z.custom<T>())
      .returns(z.union([z.string(), z.number()])),
    render: z.function().args(z.custom<T>()).returns(z.custom<ReactNode>()),
  });

// Date presets

const datePresets = ["thisWeek", "thisMonth", "lastThreeMonths", "thisYear"] as const;

export const datePresetSchema = z.enum(datePresets);

export function isDatePresetValid(datePreset: unknown): datePreset is ZDatePreset {
  return typeof datePreset === "string" && (datePresets as readonly string[]).includes(datePreset);
}

// Chart metrics

export const activityOverviewChartMetricSchema = z.enum(["payments", "enrollments", "chapterViews"]);
export const studentActivityOverviewChartMetricSchema = z.enum(["chapterViews", "chapterCompletions"]);
export const referralsChartMetricSchema = z.enum(["referrals"]);
export const timeRangeSchema = z.enum(["7d", "30d", "6m"]);
export const timestampVariantSchema = z.enum(["Google Takeout", "Screenshot", "Simple"]);

// Media

export const uploadProgressSchema = z.object({
  fileId: z.string(),
  fileKey: z.string(),
  percent: z.number(),
  status: z.string(),
});

export const uploadControllerSchema = z.object({
  fileId: z.string(),
  controller: z.custom<AbortController>(),
});

// Testimonials

export const mockReviewCreateSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  text: z.string().min(1),
  rating: z.coerce.number().int().min(1).max(5),
  sortPosition: z.coerce.number(),

  profileImageId: z.string().min(1),
});

export const mockReviewDBSchema = mockReviewCreateSchema.extend({
  teacherId: z.string().min(1),
});

// Unorganized

export const sectionContent = z.object({
  label: z.string(),
  title: z.string(),
  subtitle: z.string(),
});

// Other

export const idSchema = z.string().uuid();
export const emailSchema = z.string().email("Invalid email format");

export const anySchema = z.any();
export const stringSchema = z.string();
export const stringOrStringArraySchema = z.union([z.string(), z.array(z.string())]);

export const dataObjectSchema = z.record(z.string(), z.union([z.string(), z.number()]));
export const genericObjectSchema = z.record(z.string(), z.any());

// Recycled

// export const imageSchema = z.object({
//   key: z.string(),
//   path: z.string(),
//   url: z.string(),
//   category: z.string(),
//   hostingService: z.string(),
//   originalName: z.string(),
//   originalSize: z.coerce.number(),
//   originalType: z.string(),
//   originalTimestamp: z.custom<bigint>((val) => typeof val === "bigint", { message: "Value must be a BigInt" }),
// });

// .regex(/^03\d{2}\s?\d{7}$/, "Invalid phone number")

// export const commentDBSchema = commentCreateSchema.extend({
//   chapterId: z.string(),
//   userId: z.string(),
//   parentCommentId: z.string().optional(),
// });

// 18 Feb 2026

// return datePresets.includes(datePreset);

// 31 Mar 2026

// export const imageSchema = ImageSchema.omit(COMMON_OMIT_FIELDS);
// export const videoSchema = VideoSchema.omit(COMMON_OMIT_FIELDS);

// export const mockReviewCreateSchema = MockReviewSchema.omit({
//   id: true,
//   teacherId: true,
//   reviewedAt: true,
//   updatedAt: true,
// }).extend({
//   rating: z.coerce.number().min(1).max(5),
// });

// export const mockReviewDBSchema = MockReviewSchema.omit({
//   id: true,
//   reviewedAt: true,
//   updatedAt: true,
// });

// export const notificationSchema = NotificationSchema.omit(COMMON_OMIT_FIELDS);

// const COMMON_OMIT_FIELDS = {
//   id: true,
//   createdAt: true,
//   updatedAt: true,
// } as const;
